import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, History, Loader2, Maximize2, MessageSquare, Search, SquarePen, X } from 'lucide-react';
import BotPage from '../../pages/app/BotPage';
import { BUCKET_LABELS, bucketOf, when } from '../../lib/recentWork';
import BotAvatar from './BotAvatar';
import { useBotLabel } from './botDisplay';
import { useProjectEligibleBots } from './ComposerBotPicker';
import { useDefaultChatBot } from './defaultBot';
import { chatPath, newChatPath } from './chatLinks';
import { useBotChatSearchQuery, useBotChatsQuery, useMyBotsQuery, useProjectChatsQuery } from '../../hooks/queries';
import { ProjectTile } from '../layout/SidebarParts';
import { cn } from '../../lib/utils';
import ChatUnreadDot from '../unread/ChatUnreadDot';

const stored = (key, fallback) => {
  try { return localStorage.getItem(key) ?? fallback; } catch { return fallback; }
};
const store = (key, value) => {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
};


/** One schedule is one row: its latest run, with how many runs it made. */
const foldSchedules = (chats) => {
  const bySchedule = new Map();
  const rows = [];
  chats.forEach((c) => {
    if (!c.schedule_id) { rows.push(c); return; }
    const row = bySchedule.get(c.schedule_id);
    if (row) { row.runs.push(c); return; }
    const first = { ...c, runs: [c] };
    bySchedule.set(c.schedule_id, first);
    rows.push(first);
  });
  return rows;
};

const NO_PROJECT = 'none';

/**
 * Every chat of a project with every bot (project), or of a bot in every project (bot):
 * what waits for the person first, then by day (or grouped by bot / by project), a filter
 * per bot / per project and a search. A schedule's runs are one row; older chats fold
 * away until asked for.
 */
export const ChatHistory = ({ project, bot, onOpen, className }) => {
  const { t, i18n } = useTranslation();
  const botLabel = useBotLabel();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const id = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(id); }, [q]);
  const search = debounced.length >= 2 ? debounced : '';
  const ofBot = !!bot;
  const projectChats = useProjectChatsQuery(ofBot ? null : project?.id, search);
  const botChats = useBotChatsQuery(ofBot && !search ? bot.id : null);
  const botFound = useBotChatSearchQuery(bot?.id, null, search, { enabled: ofBot && !!search });
  const { data, isLoading } = !ofBot ? projectChats : search ? botFound : botChats;
  const { data: mine } = useMyBotsQuery();
  const bots = useMemo(() => new Map((mine?.bots || []).map((b) => [b.id, b])), [mine]);
  const all = useMemo(() => (ofBot ? data?.chats || [] : (data?.chats || []).filter((c) => bots.has(c.bot_id))),
    [ofBot, data, bots]);
  // The other side of the list: a project's chats split by bot, a bot's by project.
  const pivot = ofBot ? 'project' : 'bot';
  const keyOf = (c) => (ofBot ? c.project_id || NO_PROJECT : c.bot_id);
  const places = useMemo(() => new Map(all.map((c) => [c.project_id || NO_PROJECT, c.project])), [all]);
  const labelOf = (key) => (ofBot ? places.get(key)?.name || t('common.no_project') : botLabel(bots.get(key)));
  const iconOf = (key) => {
    if (!ofBot) return <BotAvatar bot={bots.get(key)} size="xs" />;
    const place = places.get(key);
    return place ? <ProjectTile project={place} /> : <span className="w-[18px] flex justify-center"><MessageSquare className="w-3.5 h-3.5 text-muted-foreground" /></span>;
  };
  const [filter, setFilter] = useState('all');
  const groupKey = ofBot ? 'bb.botChats.groupBy' : 'bb.chats.groupBy';
  const [groupBy, setGroupByState] = useState(() => stored(groupKey, 'date'));
  const setGroupBy = (v) => { setGroupByState(v); store(groupKey, v); };
  const [showOlder, setShowOlder] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [openSchedules, setOpenSchedules] = useState(() => new Set());

  const counts = useMemo(() => all.reduce((m, c) => { const k = ofBot ? c.project_id || NO_PROJECT : c.bot_id; return m.set(k, (m.get(k) || 0) + 1); }, new Map()), [all, ofBot]);
  const visible = filter === 'all' ? all : all.filter((c) => keyOf(c) === filter);

  const meta = (c) => {
    if (c.pending_approvals > 0) return <span className="text-[11.5px] font-medium text-orange-600 dark:text-attention-text whitespace-nowrap">{t('bots.state.needs_you')}</span>;
    if (c.working) return <span className="text-[11.5px] text-muted-foreground whitespace-nowrap">{t('bots.state.working')}</span>;
    return <span className="text-[11.5px] text-muted-foreground whitespace-nowrap tabular-nums">{when(c.updated_at || c.created_at, i18n.language)}</span>;
  };
  // A bot's chat names its project (a project's chat names its bot with the avatar).
  const place = (c) => (ofBot && groupBy !== pivot && filter === 'all' ? (
    <span className="max-w-[40%] truncate text-[11.5px] text-muted-foreground" data-testid="chat-row-project">
      {c.project?.name || t('common.no_project')}
    </span>
  ) : null);
  const row = (c, { indent = false } = {}) => {
    const runs = c.runs?.length || 0;
    const scheduled = runs > 0;
    const open = scheduled && openSchedules.has(c.schedule_id);
    return (
      <React.Fragment key={c.id}>
        <div className={cn('group flex items-center gap-2 rounded-lg hover:bg-accent transition-colors', indent && 'ml-7')}>
          <button type="button" onClick={() => onOpen(c)} data-testid="project-chat-row"
            title={ofBot ? [c.title, c.project?.name].filter(Boolean).join(' · ') : undefined}
            className="flex-1 min-w-0 flex items-center gap-2.5 h-9 px-2 text-left">
            {ofBot ? iconOf(c.project_id || NO_PROJECT)
              : <BotAvatar bot={bots.get(c.bot_id)} size="xs" state={c.working ? 'working' : null} />}
            <span className={cn('flex-1 min-w-0 truncate text-[13px] text-foreground', c.unread && 'font-medium')}>
              {scheduled ? (c.schedule?.title || c.title) : (c.title || t('bots.chat.new_chat'))}
            </span>
            {c.unread && !(c.pending_approvals > 0) && <ChatUnreadDot />}
            {place(c)}
            {scheduled ? (
              <span className="text-[11.5px] text-muted-foreground whitespace-nowrap">{t('bots.chat.runs', { count: runs })}</span>
            ) : meta(c)}
          </button>
          {scheduled && runs > 1 && (
            <button type="button" aria-expanded={open} aria-label={t('bots.chat.runs', { count: runs })}
              onClick={() => setOpenSchedules((prev) => { const n = new Set(prev); if (n.has(c.schedule_id)) n.delete(c.schedule_id); else n.add(c.schedule_id); return n; })}
              className="w-7 h-7 mr-1 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground">
              <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-90')} />
            </button>
          )}
        </div>
        {open && c.runs.map((r) => (
          <button key={r.id} type="button" onClick={() => onOpen(r)}
            className="ml-7 w-[calc(100%-1.75rem)] flex items-center gap-2 h-8 px-2 rounded-lg text-left text-[12.5px] text-muted-foreground hover:text-foreground hover:bg-accent">
            <span className="flex-1 truncate">{r.title}</span>{meta(r)}
          </button>
        ))}
      </React.Fragment>
    );
  };
  const header = (label, count, tone = 'text-muted-foreground', icon = null) => (
    <div className={cn('flex items-center gap-2 px-2 pb-1 text-[11.5px] font-semibold', tone)}>
      {icon}
      <span className="truncate">{label}</span>
      {count != null && <span className="font-normal text-muted-foreground/70">{count}</span>}
    </div>
  );

  let body;
  if (search) {
    body = visible.length ? visible.map((c) => (
      <div key={c.id}>
        {row(c)}
        {c.snippet && <p className="ml-9 -mt-1 mb-1 pr-2 text-[11.5px] text-muted-foreground line-clamp-2">{c.snippet}</p>}
      </div>
    )) : <p className="px-2 py-6 text-center text-sm text-muted-foreground">{t('bots.chat.search_empty')}</p>;
  } else if (groupBy === pivot) {
    const order = [...new Set(visible.map(keyOf))];
    body = order.map((key) => {
      const list = foldSchedules(visible.filter((c) => keyOf(c) === key));
      const expanded = openGroups.has(key);
      return (
        <section key={key} className="space-y-px">
          {header(labelOf(key), list.length, 'text-foreground', iconOf(key))}
          {(expanded ? list : list.slice(0, 3)).map((c) => row(c))}
          {list.length > 3 && !expanded && (
            <button type="button" onClick={() => setOpenGroups((prev) => new Set(prev).add(key))}
              className="ml-7 h-8 px-2 text-[12.5px] text-muted-foreground hover:text-foreground">
              {t('bots.chat.show_all', { count: list.length })}
            </button>
          )}
        </section>
      );
    });
  } else {
    const waiting = visible.filter((c) => c.pending_approvals > 0);
    const rest = visible.filter((c) => !(c.pending_approvals > 0));
    const scheduled = foldSchedules(rest.filter((c) => c.schedule_id));
    const plain = rest.filter((c) => !c.schedule_id);
    const buckets = ['today', 'yesterday', 'week', 'older'].map((key) => [key, plain.filter((c) => bucketOf(c.updated_at || c.created_at) === key)]);
    const labels = BUCKET_LABELS;
    body = (
      <>
        {waiting.length > 0 && (
          <section className="space-y-px">{header(t('bots.chat.group_needs_you'), null, 'text-orange-600 dark:text-attention-text')}{waiting.map((c) => row(c))}</section>
        )}
        {buckets.filter(([key, list]) => list.length && key !== 'older').map(([key, list]) => (
          <section key={key} className="space-y-px">{header(t(labels[key]))}{list.map((c) => row(c))}</section>
        ))}
        {scheduled.length > 0 && (
          <section className="space-y-px">{header(t('bots.chat.group_scheduled'))}{scheduled.map((c) => row(c))}</section>
        )}
        {buckets[3][1].length > 0 && (
          <section className="space-y-px">
            {header(t(labels.older))}
            {showOlder ? buckets[3][1].map((c) => row(c)) : (
              <button type="button" onClick={() => setShowOlder(true)} data-testid="project-chats-older"
                className="ml-7 h-8 px-2 text-[12.5px] text-muted-foreground hover:text-foreground">
                {t('bots.chat.show_older', { count: buckets[3][1].length })}
              </button>
            )}
          </section>
        )}
      </>
    );
  }

  const chip = (key, label, count, icon) => (
    <button key={key} type="button" onClick={() => setFilter(key)} aria-pressed={filter === key} data-testid="project-chats-filter"
      className={cn('inline-flex items-center gap-1.5 h-7 rounded-full border text-[12px] transition-colors max-w-full',
        icon ? 'pl-0.5 pr-2.5' : 'px-2.5',
        filter === key ? 'bg-accent border-transparent text-foreground font-medium' : 'border-border text-muted-foreground hover:text-foreground')}>
      {icon}
      <span className="truncate">{label}</span>
      <span className="text-muted-foreground/70 tabular-nums">{count}</span>
    </button>
  );

  return (
    <div className={cn('flex flex-col min-h-0', className)}>
      <div className="px-3 pt-3 pb-2 space-y-2.5 flex-shrink-0">
        <div className="flex items-center gap-2">
          <label className="flex-1 min-w-0 flex items-center gap-2 h-8 px-2.5 rounded-lg border border-border focus-within:border-amber-500/60">
            <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('bots.chat.search_placeholder')}
              className="flex-1 min-w-0 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70" data-testid="project-chats-search" />
          </label>
          <div role="tablist" className="flex p-[3px] rounded-full bg-muted text-[12px] flex-shrink-0">
            {['date', pivot].map((v) => (
              <button key={v} type="button" role="tab" aria-selected={groupBy === v} onClick={() => setGroupBy(v)}
                data-testid={`project-chats-by-${v}`}
                className={cn('h-6 px-2.5 rounded-full transition-colors', groupBy === v ? 'bg-background shadow-sm font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}>
                {t(v === 'date' ? 'bots.chat.by_date' : v === 'bot' ? 'bots.chat.by_bot' : 'bots.chat.by_project')}
              </button>
            ))}
          </div>
        </div>
        {counts.size > 1 && (
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t(ofBot ? 'bots.chat.filter_by_project' : 'bots.chat.filter_by_bot')}>
            {chip('all', t('common.all'), all.length)}
            {[...counts.entries()].map(([key, n]) => chip(key, labelOf(key), n, iconOf(key)))}
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3 space-y-3" data-testid="project-chats-list">
        {isLoading ? null : all.length === 0 && !search ? (
          <p className="px-2 py-8 text-center text-sm text-muted-foreground">{t(ofBot ? 'bots.chat.no_chats_bot' : 'bots.chat.no_chats')}</p>
        ) : body}
      </div>
    </div>
  );
};

/**
 * A project's chat, next to its work: chat with any of its bots without
 * leaving the project (the bot is picked in the composer), or browse every
 * chat of the project. The chat in Chats, full width, is one click away.
 */
const ProjectChatPanel = ({ project, mode, botId, chatId, onChange, onClose, className }) => {
  const { t } = useTranslation();
  const eligible = useProjectEligibleBots(project);
  const { data } = useProjectChatsQuery(project?.id);
  const chats = data?.chats || [];
  // Without a chosen bot: the one used here last, else the project's first, else BusyBee
  // (components/bots/defaultBot.js), the same bot a new chat from anywhere else starts with.
  const fallback = useDefaultChatBot(project, { enabled: !eligible.some((b) => b.id === botId) });
  const bot = eligible.some((b) => b.id === botId) || (botId && botId === fallback.queen?.id) ? botId
    : fallback.ready ? fallback.bot?.id : null;
  const fullPath = !bot ? null : chatId ? chatPath(chatId) : newChatPath({ botId: bot, projectId: project.id });
  const icon = 'inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent';
  const historyOn = mode === 'chats';

  const actions = (extra) => (
    <>
      {extra}
      <button type="button" className={cn(icon, historyOn && 'bg-accent text-foreground')} aria-pressed={historyOn}
        onClick={() => onChange({ mode: historyOn ? 'chat' : 'chats' })} title={t('bots.chat.chats_title')}
        aria-label={t('bots.chat.chats_title')} data-testid="project-chats-toggle">
        <History className="w-4 h-4" />
      </button>
      {!historyOn && (
        <button type="button" className={icon} onClick={() => onChange({ mode: 'chat', chat: null })}
          title={t('bots.chat.new_chat')} aria-label={t('bots.chat.new_chat')} data-testid="project-chat-new">
          <SquarePen className="w-4 h-4" />
        </button>
      )}
      {!historyOn && fullPath && (
        <Link to={fullPath} className={icon} title={t('bots.chat.open_full')} aria-label={t('bots.chat.open_full')} data-testid="project-chat-full">
          <Maximize2 className="w-4 h-4" />
        </Link>
      )}
      <button type="button" className={icon} onClick={onClose} title={t('bots.chat.close_panel')} aria-label={t('bots.chat.close_panel')}
        data-testid="project-chat-close">
        <X className="w-4 h-4" />
      </button>
    </>
  );
  const head = (title, extra, back) => (
    <div className="flex items-center gap-1 h-14 px-3 border-b border-border flex-shrink-0">
      {back}
      <h2 className="flex-1 min-w-0 truncate text-sm font-semibold text-foreground px-1">{title}</h2>
      {actions(extra)}
    </div>
  );

  return (
    <div className={cn('flex flex-col min-h-0 bg-card', className)} data-testid="project-chat-panel">
      {historyOn ? (
        <>
          {head(
            <span className="flex items-center gap-1.5">{t('bots.chat.chats_title')}<span className="font-normal text-muted-foreground">{chats.length || ''}</span></span>,
            null,
            <button type="button" className={icon} onClick={() => onChange({ mode: 'chat' })} aria-label={t('common.back')}>
              <ChevronLeft className="w-4 h-4" />
            </button>,
          )}
          <ChatHistory project={project} className="flex-1"
            onOpen={(c) => onChange({ mode: 'chat', bot: c.bot_id, chat: c.id })} />
        </>
      ) : !bot ? (
        <>
          {head(t('bots.chat.new_chat'))}
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        </>
      ) : (
        <div className="flex-1 min-h-0">
          <BotPage key={bot} embedded botId={bot}
            embedParams={{ project: project.id, chat: chatId || 'new' }}
            onEmbedParams={(p) => onChange({ mode: 'chat', bot, chat: p.chat && p.chat !== 'new' ? p.chat : null })}
            onEmbedBot={(id) => onChange({ mode: 'chat', bot: id, chat: null })}
            embedToolbar={({ title, computerButton, detailsButton }) => head(title || t('bots.chat.new_chat'), (
              <>{computerButton}{detailsButton}</>
            ))} />
        </div>
      )}
    </div>
  );
};

export default ProjectChatPanel;
